import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Modal, Switch, Radio, Alert } from 'antd';
import { toast, Bounce } from 'react-toastify';
import { Button } from 'components/Button';
import FilterSelect from 'components/Form/filterSelect';
import { getIssuancesApi, reviewRejectionApi } from 'api/services/uniformManagement';
import { useUniformMgmt } from '../../context/uniformMgmtContext';
import { itemRender, defaultPagination, pageSizeOptions, handlePageChange } from 'utils/table';
import { formatDateHandler } from 'utils/common';
import { rejectionReasonOptions, getStatusBadge } from '../../constants';
import Breadcrumbs from 'components/Breadcrumbs';

const RejectionReview = () => {
  const navigate = useNavigate();
  const { warehouseOptions, warehousesLoading } = useUniformMgmt();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageSettings, setPageSettings] = useState({ ...defaultPagination });
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedIssuance, setSelectedIssuance] = useState(null);
  const [reviewAction, setReviewAction] = useState('APPROVED');
  const [returnWarehouse, setReturnWarehouse] = useState(null);
  const [creditStock, setCreditStock] = useState(true);
  const [reviewLoading, setReviewLoading] = useState(false);

  const fetchData = useCallback(() => {
    setLoading(true);
    getIssuancesApi({
      has_rejection: true,
      page: pageSettings.page,
      page_size: pageSettings.pageSize
    }).then(response => {
      setData(response.data?.results || []);
      setPageSettings(prev => ({ ...prev, total: response.data?.count || 0 }));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [pageSettings.page, pageSettings.pageSize]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleChange = (pagination, filters, sorter) => {
    setPageSettings(handlePageChange(pagination, filters, sorter, pageSettings));
  };

  const openReview = (record) => {
    setSelectedIssuance(record);
    setReviewAction('APPROVED');
    setReturnWarehouse(null);
    setCreditStock(true);
    setShowReviewModal(true);
  };

  const handleReview = () => {
    const rejectionId = selectedIssuance?.rejection_id;
    if (!rejectionId) {
      toast.error('No rejection record found for this issuance', { position: "top-right", autoClose: 3000, transition: Bounce });
      return;
    }

    setReviewLoading(true);
    reviewRejectionApi(rejectionId, {
      review_status: reviewAction,
      return_to_warehouse: reviewAction === 'APPROVED' && returnWarehouse ? returnWarehouse.value : null,
      credit_stock: reviewAction === 'APPROVED' ? creditStock : true
    }).then(() => {
      toast.success(`Rejection ${reviewAction.toLowerCase()}`, { position: "top-right", autoClose: 3000, transition: Bounce });
      setReviewLoading(false);
      setShowReviewModal(false);
      setSelectedIssuance(null);
      fetchData();
    }).catch(e => {
      setReviewLoading(false);
      const data = e.response?.data;
      const msg =
        data?.error ||
        data?.detail ||
        data?.non_field_errors?.[0] ||
        (data && typeof data === 'object'
          ? Object.entries(data)
              .filter(([k]) => k !== 'status')
              .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`)
              .join(' | ')
          : null) ||
        'Review failed';
      toast.error(msg, { position: "top-right", autoClose: 3000, transition: Bounce });
    });
  };

  const columns = [
    {
      title: 'Employee',
      render: (_, record) => (
        <div>
          <span>{record.company_user_name}</span>
          <br /><small className="text-muted">{record.company_user_emp_no}</small>
        </div>
      )
    },
    { title: 'Uniform', dataIndex: 'uniform_item_name' },
    {
      title: 'Size', dataIndex: 'size_detail',
      render: (detail) => detail ? `${detail.code}` : '-'
    },
    { title: 'Warehouse', dataIndex: 'warehouse_name' },
    { title: 'Issued Date', dataIndex: 'issuance_date', render: (d) => formatDateHandler(d) },
    {
      title: 'Review Status',
      render: (_, record) => {
        const rs = record.rejection_review_status || 'PENDING';
        const color = rs === 'PENDING' ? '#faad14' : rs === 'APPROVED' ? '#52c41a' : '#ff4d4f';
        return <span style={{ color, fontWeight: 600 }}>{rs}</span>;
      }
    },
    {
      title: 'Action',
      render: (_, record) => {
        const reviewed = record.rejection_review_status && record.rejection_review_status !== 'PENDING';
        return (
          <div className="d-flex gap-2">
            <Button
              label={reviewed ? 'Reviewed' : 'Review'}
              className={`btn btn-sm ${reviewed ? 'btn-outline-secondary' : 'btn-primary'}`}
              disabled={reviewed}
              onClick={() => openReview(record)}
            />
            <Button label="View" icon="fa fa-eye" className="btn btn-sm btn-outline-primary"
              onClick={() => navigate(`/uniform-management/issuance/${record.id}`)} />
          </div>
        );
      }
    }
  ];

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <Breadcrumbs maintitle="Rejection Review" />

        <Table columns={columns} dataSource={data} rowKey={(r) => r.id} loading={loading}
          pagination={{
            total: pageSettings.total, pageSize: pageSettings.pageSize, current: pageSettings.page,
            pageSizeOptions, itemRender, showSizeChanger: true
          }}
          onChange={handleChange} scroll={{ x: "max-content" }} />

        <Modal
          title="Review Rejection"
          open={showReviewModal}
          onCancel={() => setShowReviewModal(false)}
          width={520}
          centered
          destroyOnClose
          footer={[
            <button key="cancel" type="button" className="btn btn-light"
              onClick={() => setShowReviewModal(false)} disabled={reviewLoading}>
              Cancel
            </button>,
            <Button
              key="submit"
              label={reviewAction === 'APPROVED' ? 'Approve Rejection' : 'Deny Rejection'}
              className={`btn ${reviewAction === 'APPROVED' ? 'btn-success' : 'btn-danger'} ms-2`}
              loading={reviewLoading}
              onClick={handleReview}
            />
          ]}
        >
          {selectedIssuance && (
            <div>
              <div className="p-3 mb-3" style={{
                background: '#F9FAFB', borderRadius: 10, border: '1px solid #F1F2F4'
              }}>
                <div className="d-flex justify-content-between mb-1">
                  <span className="text-muted" style={{ fontSize: 12 }}>Employee</span>
                  <span className="fw-semibold">{selectedIssuance.company_user_name}</span>
                </div>
                <div className="d-flex justify-content-between mb-1">
                  <span className="text-muted" style={{ fontSize: 12 }}>Uniform</span>
                  <span className="fw-semibold">{selectedIssuance.uniform_item_name}</span>
                </div>
                <div className="d-flex justify-content-between">
                  <span className="text-muted" style={{ fontSize: 12 }}>Size</span>
                  <span className="fw-semibold">{selectedIssuance.size_detail?.code || '—'}</span>
                </div>
              </div>

              <div className="mb-3">
                <label className="col-form-label">Decision</label>
                <Radio.Group
                  value={reviewAction}
                  onChange={(e) => setReviewAction(e.target.value)}
                  optionType="button"
                  buttonStyle="solid"
                  style={{ width: '100%' }}
                >
                  <Radio.Button value="APPROVED" style={{ width: '50%', textAlign: 'center' }}>
                    <i className="fa fa-check me-1" />Approve
                  </Radio.Button>
                  <Radio.Button value="DENIED" style={{ width: '50%', textAlign: 'center' }}>
                    <i className="fa fa-times me-1" />Deny
                  </Radio.Button>
                </Radio.Group>
              </div>

              {reviewAction === 'APPROVED' && (
                <>
                  <div className="mb-3 d-flex align-items-center justify-content-between p-3"
                    style={{ background: '#F9FAFB', borderRadius: 10 }}>
                    <div>
                      <div className="fw-semibold" style={{ fontSize: 14 }}>Credit stock back?</div>
                      <small className="text-muted">
                        {creditStock ? 'Stock will be returned to warehouse' : 'Item is damaged or lost'}
                      </small>
                    </div>
                    <Switch checked={creditStock} onChange={setCreditStock} />
                  </div>
                  {creditStock && (
                    <div className="mb-2">
                      <label className="col-form-label">
                        Return to Warehouse <small className="text-muted">(optional override)</small>
                      </label>
                      <FilterSelect isClearable onChange={setReturnWarehouse} optionLabel="label" optionValue="value"
                        options={warehouseOptions} value={returnWarehouse}
                        placeholder="Use original warehouse" isLoading={warehousesLoading} />
                    </div>
                  )}
                </>
              )}

              {reviewAction === 'DENIED' && (
                <Alert
                  type="warning"
                  showIcon
                  message="The issuance status will revert to Accepted and the employee will be notified."
                  style={{ borderRadius: 10 }}
                />
              )}
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
};

export default RejectionReview;
